import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

import Input from '../../../components/ui/Input';


const ServiceOfferingsStep = ({ data, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    primaryServices: data?.primaryServices || [],
    geographicCoverage: data?.geographicCoverage || [],
    certifications: data?.certifications || [],
    insuranceCoverage: data?.insuranceCoverage || '',
    capacityVolume: data?.capacityVolume || '',
    ...data
  });

  const [errors, setErrors] = useState({});

  const serviceOptions = [
    { value: 'flight_booking', label: 'Flight Booking & Ticketing' },
    { value: 'hotel_accommodation', label: 'Hotel & Accommodation' },
    { value: 'car_rental', label: 'Car Rental Services' },
    { value: 'ground_transport', label: 'Ground Transportation' },
    { value: 'travel_insurance', label: 'Travel Insurance' },
    { value: 'visa_assistance', label: 'Visa & Documentation Assistance' },
    { value: 'corporate_travel', label: 'Corporate Travel Management' },
    { value: 'group_travel', label: 'Group Travel Services' },
    { value: 'emergency_support', label: '24/7 Emergency Support' },
    { value: 'concierge', label: 'Concierge Services' }
  ];

  const geographicOptions = [
    { value: 'north_america', label: 'North America' },
    { value: 'south_america', label: 'South America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia', label: 'Asia' },
    { value: 'africa', label: 'Africa' },
    { value: 'oceania', label: 'Oceania' },
    { value: 'middle_east', label: 'Middle East' }
  ];

  const certificationOptions = [
    { value: 'iata', label: 'IATA Accreditation' },
    { value: 'asta', label: 'ASTA Membership' },
    { value: 'iso_9001', label: 'ISO 9001 Certification' },
    { value: 'iso_27001', label: 'ISO 27001 (Security)' },
    { value: 'gdpr', label: 'GDPR Compliant' },
    { value: 'pci_dss', label: 'PCI DSS Certified' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (formData?.primaryServices?.length === 0) {
      newErrors.primaryServices = 'Select at least one service';
    }
    if (formData?.geographicCoverage?.length === 0) {
      newErrors.geographicCoverage = 'Select at least one region';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext?.(formData);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Service Offerings</h2>
        <p className="text-muted-foreground">
          Detail your service capabilities and coverage areas.
        </p>
      </div>
      {/* Form */}
      <div className="space-y-6">
        {/* Primary Services */}
        <div className="space-y-2">
          <Select
            label="Primary Services"
            required
            multiple
            searchable
            options={serviceOptions}
            value={formData?.primaryServices}
            onChange={(value) => handleChange('primaryServices', value)}
            error={errors?.primaryServices}
            description="Select all services your organization provides"
            placeholder="Select services"
          />
        </div>

        {/* Geographic Coverage */}
        <div className="space-y-2">
          <Select
            label="Geographic Coverage"
            required
            multiple
            options={geographicOptions}
            value={formData?.geographicCoverage}
            onChange={(value) => handleChange('geographicCoverage', value)}
            error={errors?.geographicCoverage}
            description="Select all regions where you provide services"
            placeholder="Select regions"
          />
        </div>

        {/* Certifications */}
        <div className="space-y-2">
          <Select
            label="Industry Certifications"
            multiple
            searchable
            options={certificationOptions}
            value={formData?.certifications}
            onChange={(value) => handleChange('certifications', value)}
            description="Select all applicable certifications"
            placeholder="Select certifications (optional)"
          />
        </div>

        {/* Additional Details */}
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Insurance Coverage Amount"
            value={formData?.insuranceCoverage}
            onChange={(e) => handleChange('insuranceCoverage', e?.target?.value)}
            placeholder="$5,000,000"
            description="Liability insurance coverage"
          />

          <Input
            label="Monthly Capacity Volume"
            value={formData?.capacityVolume}
            onChange={(e) => handleChange('capacityVolume', e?.target?.value)}
            placeholder="500 bookings/month"
            description="Estimated monthly capacity"
          />
        </div>

        {/* Service Highlights */}
        <div className="glass-container rounded-xl p-6 bg-primary/5">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">Service Quality Standards</h4>
              <p className="text-sm text-muted-foreground">
                All partners must maintain 24/7 customer support, comply with industry regulations, and provide transparent pricing. Additional requirements may apply based on selected services.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex justify-between pt-6 border-t border-white/20">
        <Button
          variant="outline"
          iconName="ArrowLeft"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          iconName="ArrowRight"
          iconPosition="right"
          onClick={handleNext}
        >
          Continue to Documentation
        </Button>
      </div>
    </div>
  );
};

export default ServiceOfferingsStep;